import { useEffect, useState, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { flush, onSent, remove, snapshot, subscribe } from '../lib/outbox.js';
import { useMe } from '../hooks/data.js';
import { useToast } from './toast.jsx';
import { Icon } from '../components/Icon.jsx';
import { Sheet } from '../components/Sheet.jsx';
import { dateTimeLabel } from '../lib/format.js';

export const useOutbox = () => useSyncExternalStore(subscribe, snapshot);

/**
 * Sendet ausstehende Buchungen automatisch: beim Start, beim Wiederverbinden und nach der Anmeldung
 * (sobald /me den Benutzer kennt). Lädt danach die betroffenen Daten neu.
 */
export function OutboxSync() {
  const qc = useQueryClient();
  const me = useMe();
  const toast = useToast();
  const uid = me.data?.uid ?? null;

  // Nach dem Nachsenden Daten neu laden und einmal gesammelt melden
  useEffect(() => {
    let n = 0;
    let t = null;
    const off = onSent((item) => {
      qc.invalidateQueries({ queryKey: [item.kind === 'material' ? 'material' : 'leistungen', item.nummer] });
      n++;
      clearTimeout(t);
      t = setTimeout(() => { toast({ text: `${n} ${n === 1 ? 'Buchung' : 'Buchungen'} nachgesendet` }); n = 0; }, 600);
    });
    return () => { off(); clearTimeout(t); };
  }, [qc, toast]);

  useEffect(() => {
    if (!uid) return undefined;
    const run = () => flush(uid);
    run();
    window.addEventListener('online', run);
    return () => window.removeEventListener('online', run);
  }, [uid]);

  return null;
}

/** Zeitpunkt der letzten erfolgreichen Übertragung aus der Warteschlange (für «Alles übertragen um …»). */
function useZuletztUebertragen() {
  const [am, setAm] = useState(null);
  useEffect(() => onSent(() => setAm(new Date())), []);
  return am;
}
const uhrzeit = (d) => d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });

/**
 * Sync-Zustand in Klartext (nie nur über Farbe): «Auf dem Gerät gespeichert», «Nicht übertragen», kurz danach
 * «Alles übertragen um 14:32». Antippen zeigt die Einträge.
 */
export function OutboxBanner({ dark = false }) {
  const items = useOutbox();
  const me = useMe();
  const zuletzt = useZuletztUebertragen();
  const [open, setOpen] = useState(false);
  const mine = items.filter((i) => !me.data || i.uid === me.data.uid);
  if (!mine.length) {
    return zuletzt ? (
      <div className={`outbox ok${dark ? ' dark' : ''}`} role="status">
        <Icon name="check" size={18} stroke={2.6} />
        <span style={{ flex: 1 }}>Alles übertragen um {uhrzeit(zuletzt)}</span>
      </div>
    ) : null;
  }
  const failed = mine.filter((i) => i.fehler).length;
  const text = failed
    ? `${failed} ${failed === 1 ? 'Eintrag' : 'Einträge'} nicht übertragen`
    : `${mine.length} ${mine.length === 1 ? 'Eintrag' : 'Einträge'} auf dem Gerät gespeichert, ${mine.length === 1 ? 'wird' : 'werden'} übertragen, sobald Netz da ist`;
  return (
    <>
      <button className={`outbox${failed ? ' err' : ''}${dark ? ' dark' : ''}`} onClick={() => setOpen(true)} aria-label={`${text}. Details anzeigen`}>
        <Icon name={failed ? 'warn' : 'upload'} size={18} stroke={2.4} />
        <span style={{ flex: 1 }}>{text}</span>
        <span style={{ fontWeight: 800, textDecoration: 'underline', textUnderlineOffset: 3 }}>Details</span>
      </button>
      {open && <OutboxSheet items={mine} uid={me.data?.uid} onClose={() => setOpen(false)} />}
    </>
  );
}

function OutboxSheet({ items, uid, onClose }) {
  const [busy, setBusy] = useState(false);
  const retry = async () => { setBusy(true); await flush(uid); setBusy(false); };
  return (
    <Sheet title="Noch nicht übertragen" onClose={onClose}>
      <span className="sub">Die Einträge sind auf diesem Gerät gespeichert und werden automatisch übertragen, sobald wieder Netz da ist. Doppelt gebucht wird dabei nichts. Nicht abmelden, bis alles übertragen ist.</span>
      <div className="card list" style={{ padding: '2px 12px', background: 'var(--bg)' }}>
        {items.map((i) => (
          <div key={i.id} className="listrow" style={{ alignItems: 'flex-start' }}>
            <span className="stack" style={{ flex: 1, gap: 2 }}>
              <span style={{ fontSize: 14, fontWeight: 800 }}>{i.label}</span>
              <span className="sub" style={{ fontSize: 12 }}>{i.nummer}, erfasst {dateTimeLabel(i.erstelltAm)}</span>
              <SyncStatus fehler={i.fehler} />
              {i.fehler && <span className="error" style={{ fontSize: 13 }}>{i.fehler}</span>}
            </span>
            {i.fehler && <button className="btn secondary" style={{ minHeight: 48 }} onClick={() => remove(i.id)}>Verwerfen</button>}
          </div>
        ))}
      </div>
      <button className="btn lg block" onClick={retry} disabled={busy || !uid}>{busy ? 'Wird übertragen …' : 'Jetzt übertragen'}</button>
    </Sheet>
  );
}

/** Zustand eines Eintrags: Icon und Wort, nicht nur Farbe. */
export function SyncStatus({ fehler }) {
  return fehler
    ? <span className="pill warn"><Icon name="warn" size={12} stroke={2.6} />Nicht übertragen</span>
    : <span className="pill info"><Icon name="upload" size={12} stroke={2.6} />Auf dem Gerät gespeichert</span>;
}

/** Wartende Einträge dieses Auftrags (Zeit oder Material) mit Zustand, damit niemand doppelt erfasst. */
export function WartendeEintraege({ nr, kind }) {
  const items = useOutbox();
  const me = useMe();
  const liste = items.filter((i) => i.nummer === nr && i.kind === kind && (!me.data || i.uid === me.data.uid));
  if (!liste.length) return null;
  return (
    <div className="stack" style={{ gap: 6, padding: '8px 0' }}>
      {liste.map((i) => (
        <div key={i.id} className="row" style={{ alignItems: 'flex-start', padding: '6px 0', borderTop: '1px solid var(--line-2)' }}>
          <span className="stack" style={{ flex: 1, gap: 4 }}>
            <span style={{ fontSize: 15, fontWeight: 700 }}>{i.label}</span>
            <SyncStatus fehler={i.fehler} />
          </span>
        </div>
      ))}
    </div>
  );
}
